import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useStore } from "../../../../stores/store";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import useAxiosPrivate from "../../../../hooks/useAxiosPrivate";
import * as yup from "yup";
import { useSetMenu } from "../../../../hooks/useSetMenu";
import FormLayout from '../../../Generic/FormLayout';
import FormInput from '../../../Generic/FormInput';
import FormCheckbox from '../../../Generic/FormCheckbox';

const Add = () => {
  const { mainStore } = useStore();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const axiosPrivate = useAxiosPrivate();
  useSetMenu(mainStore.ResearchTeamAdminMenuType, mainStore.ResearchTeamAdminMenuNo);

  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const onError = (errors, e) =>
    enqueueSnackbar(t("onErrorSubmit"), { variant: "error" });

  const schema = yup.object({
    nameEN: yup.string().required(t('requiredField')).max(200, t('maxLength', { max: 200 })),
    memberOrder: yup.number().required(t('requiredField')).min(1, t('minValue', { min: 1 })),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      memberOrder: 1,
      isVisible: true,
    }
  });

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
  };

  const onSubmit = async (data) => {
    try {
      const formData = new FormData();
      Object.keys(data).forEach(key => {
        if (data[key] !== undefined && data[key] !== null) {
          formData.append(key, data[key]);
        }
      });

      if (selectedFile) {
        formData.append('formFile', selectedFile);
      }

      await axiosPrivate.post('/ResearchTeamMembers', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      enqueueSnackbar(t("successAdd"), { variant: "success" });
      navigate("/Admin/ResearchTeam");
    } catch (error) {
      enqueueSnackbar(t("errorAdd"), { variant: "error" });
    }
  };

  const onCancel = () => navigate(-1);

  return (
    <FormLayout title={t("researchTeamMemberCreateTitle") || "Add Team Member"} onSubmit={handleSubmit(onSubmit, onError)} onCancel={onCancel}>
      <FormInput label="order" name="memberOrder" type="number" register={register} errors={errors} colSize={2} />
      <FormInput label="memberType" name="memberType" register={register} errors={errors} colSize={4} />
      <FormInput label="email" name="email" register={register} errors={errors} colSize={6} />
      <FormInput label="nameEN" name="nameEN" register={register} errors={errors} colSize={4} />
      <FormInput label="nameBG" name="nameBG" register={register} errors={errors} colSize={4} />
      <FormInput label="nameSQ" name="nameSQ" register={register} errors={errors} colSize={4} />
      <FormInput label="titleEN" name="titleEN" register={register} errors={errors} colSize={4} />
      <FormInput label="titleBG" name="titleBG" register={register} errors={errors} colSize={4} />
      <FormInput label="titleSQ" name="titleSQ" register={register} errors={errors} colSize={4} />
      <FormInput label="affiliationEN" name="affiliationEN" register={register} errors={errors} colSize={12} />
      <FormInput label="affiliationBG" name="affiliationBG" register={register} errors={errors} colSize={6} />
      <FormInput label="affiliationSQ" name="affiliationSQ" register={register} errors={errors} colSize={6} />
      <FormInput label="contributionEN" name="contributionEN" register={register} errors={errors} colSize={12} isTextArea={true} />
      <FormInput label="contributionBG" name="contributionBG" register={register} errors={errors} colSize={12} isTextArea={true} />
      <FormInput label="contributionSQ" name="contributionSQ" register={register} errors={errors} colSize={12} isTextArea={true} />

      {/* Avatar Upload Section */}
      <div className="col-12 mb-3">
        <label className="form-label">{t("avatar") || "Avatar"}</label>
        <div className="d-flex align-items-start gap-3">
          {/* Avatar Preview */}
          <div className="rounded-circle d-flex align-items-center justify-content-center"
            style={{
              width: '80px',
              height: '80px',
              backgroundColor: '#112d4e',
              overflow: 'hidden',
              flexShrink: 0
            }}>
            {previewUrl ? (
              <img src={previewUrl} alt="Preview"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <i className="bi bi-person-circle text-white" style={{ fontSize: '2.5rem' }}></i>
            )}
          </div>

          {/* Upload Controls */}
          <div className="flex-grow-1">
            <input
              type="file"
              className="form-control"
              accept="image/*"
              onChange={handleFileChange}
            />
            <small className="text-muted d-block mt-1">
              {t("avatarUploadHint") || "Upload an image (JPG, PNG). Leave empty for default avatar."}
            </small>
            {previewUrl && (
              <button type="button" className="btn btn-outline-danger btn-sm mt-2" onClick={removeImage}>
                <i className="bi bi-trash me-1"></i> {t("removeImage") || "Remove Image"}
              </button>
            )}
          </div>
        </div>
      </div>

      <FormInput label="avatarColor" name="avatarColor" register={register} errors={errors} colSize={4} />
      <FormCheckbox label="isVisible" register={register} name="isVisible" colSize={4} />
    </FormLayout>
  );
};

export default Add;
